import { FC } from 'react';
import { Button, Menu } from 'react-daisyui';
import { cn } from '@/utils/classNames.ts';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { useTranslation } from 'react-i18next';
import { navData } from '@/layouts/Default/config-nav.tsx';
import { adminNavData } from '@/layouts/Default/config-nav-admin.tsx';
import { usePathname } from '@/hooks/usePathname.ts';
import { Link } from 'react-router-dom';
import { ArrowLeftStartOnRectangleIcon } from '@heroicons/react/24/solid';

type NavbarProps = {
  onClick?: () => void;
  menuClassName?: string;
  showTitles?: boolean;
};

const Navbar: FC<PropsWithClassName<NavbarProps>> = ({ className, onClick, menuClassName, showTitles = false }) => {
  const { logout } = useAuthContext();
  const { t } = useTranslation();
  const pathname = usePathname();
  const isAdmin = pathname.includes('/admin');
  const currentNavData = isAdmin ? adminNavData : navData;

  return (
    <nav className={cn('flex flex-col gap-4 justify-between grow', className)}>
      <div className="flex flex-col grow">
        <Menu className={cn('p-0 flex-nowrap grow h-1 flex flex-col gap-7 justify-center', menuClassName)}>
          {currentNavData.map((item) => {
            const isActivated = item.path === pathname;

            return (
              <Menu.Item key={item.path} className="w-full group relative">
                <Link
                  to={item.path}
                  title={item.title}
                  onClick={() => (onClick ? onClick() : null)}
                  className={cn(
                    'flex items-center rounded-2xl p-0 min-h-0 h-auto',
                    {
                      'justify-center w-14 mx-auto': !showTitles,
                      'justify-start gap-4 w-full': showTitles,
                    },
                    isAdmin
                      ? {
                          'bg-secondary text-white': isActivated,
                          'hover:bg-white/10': !isActivated,
                        }
                      : {
                          'active:bg-secondary active:bg-opacity-100 active:text-white hover:bg-secondary hover:bg-opacity-20':
                            true,
                          'bg-secondary text-white': isActivated,
                        }
                  )}
                >
                  {item.icon ? (
                    <div
                      className={cn('w-14 h-14 flex items-center justify-center rounded-2xl', {
                        'text-secondary': !isAdmin && !isActivated,
                        'text-white': isActivated || isAdmin,
                        'text-white/70': isAdmin && !isActivated,
                      })}
                    >
                      <div className="w-6 h-6">{item.icon}</div>
                    </div>
                  ) : null}
                  {showTitles && <span className="text-ellipsis line-clamp-1 text-base pr-4">{item.title}</span>}
                </Link>
                {/* Custom tooltip - only show when titles are hidden */}
                {!showTitles && (
                  <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                    <div className={cn('text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap', isAdmin ? 'bg-secondary' : 'bg-gray-900')}>
                      <div className="font-semibold text-base">{item.title}</div>
                      {item.tooltipTitle && (
                        <div className={cn('text-sm mt-1', isAdmin ? 'text-white/80' : 'text-gray-300')}>{item.tooltipTitle}</div>
                      )}
                    </div>
                  </div>
                )}
              </Menu.Item>
            );
          })}
        </Menu>
      </div>
      <div className="flex flex-col items-center">
        {showTitles ? (
          <Button
            onClick={logout}
            color="ghost"
            className={cn('!gap-4 w-full justify-start', isAdmin && 'text-white/70 hover:text-white hover:bg-white/10')}
            startIcon={<ArrowLeftStartOnRectangleIcon className="w-6 h-6" />}
          >
            {t('button.logout')}
          </Button>
        ) : (
          <button
            onClick={logout}
            className={cn(
              'w-14 h-14 flex items-center justify-center rounded-2xl transition-colors',
              isAdmin ? 'hover:bg-white/10' : 'hover:bg-gray-100'
            )}
          >
            <ArrowLeftStartOnRectangleIcon className={cn('w-6 h-6', isAdmin ? 'text-white/70' : 'text-gray-600')} />
          </button>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
